import { generateUUID } from '@lovoldsystem/ui'
import { Box, Grid, Typography, useTheme } from '@mui/material'
import { decode, encode } from 'html-entities'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill'
import { ReactComponent as FileIcon } from 'src/assets/svg/fileUploadIcon.svg'
import { GridActionButton } from 'src/components/common/GridActionButton'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormFileCoreUpload, FormSelectField, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { RepeatType } from 'src/enums'
import { isMaintenanceFrequencyValid } from './CreateMaintenanceForm.schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { ILocalityEquipmentMaintenance } from 'src/generated-types/maintenance'
import { ISimpleMainComponent } from 'src/generated-types/simple-maincomponent'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { covertToDateType, getUTCDate } from 'src/utils/function'
import { z } from 'zod'
export interface CreateMaintenanceFormProps extends ShowDialogProps {
  addMaintenance: (maintenance: ILocalityEquipmentMaintenance) => void
  initialValues?: any
  mainComponents?: ISimpleMainComponent[]
  isLocalityMaintenance: boolean
  isCustomMaintenance?: boolean
  onSuccess: (pop: (...args: any[]) => any, customerId?: EntityId, localityId?: EntityId) => void
  isLoading?: boolean
}

const CreateMaintenanceForm: React.FC<CreateMaintenanceFormProps> = ({
  pop,
  addMaintenance,
  initialValues,
  mainComponents,
  isLocalityMaintenance = false,
  isCustomMaintenance,
  onSuccess,
  isLoading = false,
}) => {
  const { t: translate, i18n } = useTranslation(['maintenance', 'common'])
  const [selectedDays, setSelectedDays] = useState<number[]>(() => {
    if (initialValues?.repeatType === RepeatType.Daily) {
      return []
    }
    return initialValues ? initialValues.weekDays.split(',').map(Number) : []
  })
  const theme = useTheme()

  const repeatTypes = Object.keys(RepeatType)
    .filter(v => isNaN(Number(v)))
    .map(t => RepeatType[t])

  const weekdays = {
    0: 'SUN',
    1: 'MON',
    2: 'TUE',
    3: 'WED',
    4: 'THU',
    5: 'FRI',
    6: 'SAT',
  }

  const getDefaultGracePeriod = (repeatType?: RepeatType) => {
    const currentRepeatType = repeatType
    switch (currentRepeatType) {
      case RepeatType.Never:
        return 1
      case RepeatType.Daily:
        return 1
      case RepeatType.Weekly:
        return 3
      case RepeatType.Monthly:
        return 7
      case RepeatType.Yearly:
        return 30
      default:
        return 1
    }
  }

  useEffect(() => {
    form.setValue('weekDays', selectedDays.map(String))
  }, [selectedDays])

  const toggleSelection = key => {
    const numKey = Number(key)
    setSelectedDays(prev => {
      const newSelectedDays = prev.includes(numKey) ? prev.filter(day => day !== numKey) : [...prev, numKey]
      form.setValue('weekDays', newSelectedDays.map(String))
      form.trigger('weekDays')
      return newSelectedDays
    })
  }

  const maintenanceSchema = z
    .object({
      name: z.string().min(1, { message: translate('required', { ns: 'common' }) }),
      repeatType: z.nativeEnum(RepeatType, {
        errorMap: () => ({ message: translate('required', { ns: 'common' }) }),
      }),
      frequency: z
        .string()
        .optional()
        .refine(val => form.getValues().repeatType === RepeatType.Never || (val && val.length > 0), {
          message: translate('required', { ns: 'common' }),
        })
        .refine(
          val => {
            if (form.getValues().repeatType === RepeatType.Never || !val) return true
            return Number.isInteger(Number(val))
          },
          {
            message: translate('decimals-are-not-allowed-error', { ns: 'maintenance' }),
          },
        )
        .refine(val => isMaintenanceFrequencyValid(val, form.getValues().repeatType), {
          message: translate('frequency-must-be-greater-than-zero-error', { ns: 'maintenance' }),
        }),
      weekDays: z.array(z.string()).refine(
        val => {
          const repeatType = form.getValues().repeatType
          return repeatType !== RepeatType.Weekly || (val && val.length > 0)
        },
        { message: translate('required-weekdays', { ns: 'maintenance' }) },
      ),
      startDate: z.date().or(z.string()).optional().nullable(),
      repeatEnds: z.date().or(z.string()).optional().nullable(),
      note: z.string().refine(
        val => {
          if (!val) return false

          const decodedValue = decode(val)
          const textContent = decodedValue.replace(/<[^>]*>/g, '').trim()
          return textContent.length > 0
        },
        { message: translate('required', { ns: 'common' }) },
      ),
      mainComponents:
        isCustomMaintenance && !isLocalityMaintenance
          ? z.array(z.number()).min(1, { message: translate('required', { ns: 'common' }) })
          : z.array(z.number()).optional(),
      fileId: z.union([z.string(), z.number()]).optional().nullable(),
      gracePeriod: z
        .union([z.string(), z.number()])
        .refine(val => !isNaN(Number(val)) && Number(val) >= 1, {
          message: translate('grace-period-error', { ns: 'maintenance' }),
        })
        .refine(
          val => {
            return Number.isInteger(Number(val))
          },
          {
            message: translate('decimals-are-not-allowed-error', { ns: 'maintenance' }),
          },
        ),
      file: z.any().or(z.null()).optional(),
    })
    .superRefine(({ startDate, repeatEnds }, ctx) => {
      const start = covertToDateType(startDate)
      const end = covertToDateType(repeatEnds)

      if (start && end && start > end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('start-date-error', { ns: 'maintenance' }),
          path: ['startDate'],
        })
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('repeat-ends-error', { ns: 'maintenance' }),
          path: ['repeatEnds'],
        })
      }
    })

  const form = useZodForm(maintenanceSchema, {
    defaultValues: { ...{ gracePeriod: 3, startDate: getUTCDate(new Date()) }, ...initialValues },
  })

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'repeatType') {
        const newGracePeriod = getDefaultGracePeriod(value.repeatType)
        form.setValue('gracePeriod', newGracePeriod)
      }
    })
    return () => subscription.unsubscribe()
  }, [form])

  const isUpdateForm = initialValues && initialValues.id

  const handleSubmit = async data => {
    const maintenanceWithId = {
      ...data,
      id: generateUUID(),
      weekDays: data.repeatType === RepeatType.Daily ? '' : selectedDays.join(','),
      startDate: data.startDate ? getUTCDate(new Date(data.startDate)) : data.startDate,
      repeatEnds: data.repeatEnds ? getUTCDate(new Date(data.repeatEnds)) : data.repeatEnds,
    }
    await addMaintenance(maintenanceWithId as ILocalityEquipmentMaintenance)
    onSuccess(pop)
  }

  return (
    <>
      <GenericDialogLayout
        title={
          !isUpdateForm
            ? translate('add-maintenance', { ns: 'maintenance' })
            : translate('edit-maintenance', { ns: 'maintenance' })
        }
        pop={pop}
        removedCloseButton={false}
        actions={[
          <GridActionButton
            variant="contained"
            key="add-button"
            height="44px"
            width="100px"
            border={0}
            buttonTextProps={{
              variant: 'subtitle2',
              fontWeight: 500,
              fontSize: '14px',
              color: theme.palette.white[theme.palette.mode],
            }}
            bgColor={theme.palette.primaryMain[theme.palette.mode]}
            shadowWeight={0}
            onClick={() => {
              form.handleSubmit(handleSubmit)()
            }}
            disabled={isLoading}
            loading={isLoading}
            buttonText={!isUpdateForm ? translate('save', { ns: 'common' }) : translate('update', { ns: 'common' })}
          />,
        ]}
      >
        <Box sx={{ flexGrow: 1 }}>
          <AppForm form={form} onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <FormTextField name="name" label={translate('maintenance-name', { ns: 'maintenance' })} />
              </Grid>
              <Grid item xs={6}>
                <FormSelectField
                  name="repeatType"
                  label={translate('repeat-type', { ns: 'maintenance' })}
                  data={repeatTypes}
                  getOptionLabel={option => RepeatType[option]}
                  renderOption={(props: any, option) => (
                    <Box {...props} key={option}>
                      {translate(RepeatType[option].toLocaleLowerCase(), { ns: 'maintenance' })}
                    </Box>
                  )}
                  defaultValue={repeatTypes?.[0] ?? ''}
                  clearOnBlur={false}
                  clearOnEscape={false}
                  isOptionEqualToValue={(o, v) => o === v}
                />
              </Grid>
              <Grid item xs={6}>
                {form.watch('repeatType') !== RepeatType.Never && (
                  <FormTextField name="frequency" type="number" label={translate('frequency', { ns: 'maintenance' })} />
                )}
              </Grid>
              <Grid item xs={6}>
                <FormTextField
                  name="gracePeriod"
                  type="number"
                  label={translate('gracePeriod', { ns: 'maintenance' })}
                />
              </Grid>
              {form.watch('repeatType') === RepeatType.Weekly && (
                <Grid item xs={12}>
                  <Box style={{ padding: '0 8px 12px' }}>
                    <Typography variant="body1" sx={{ fontWeight: '0 !important' }}>
                      {translate('repeat-on', { ns: 'maintenance' })}
                    </Typography>
                  </Box>
                  <Box className="flex gap-3 mb-4" style={{ padding: '0 8px' }}>
                    {Object.entries(weekdays).map(([key, day]) => {
                      const numKey = Number(key)
                      return (
                        <button
                          type="button"
                          style={{
                            border: `1px solid ${selectedDays.includes(numKey)
                                ? theme.palette.primaryMain[theme.palette.mode]
                                : theme.palette.secondaryMain[theme.palette.mode]
                              }`,
                            backgroundColor: selectedDays.includes(numKey)
                              ? theme.palette.primaryMain[theme.palette.mode]
                              : theme.palette.background.default,
                            color: selectedDays.includes(numKey)
                              ? theme.palette.white[theme.palette.mode]
                              : theme.palette.primaryMain[theme.palette.mode],
                            borderRadius: '4px',
                            width: '85px',
                            height: '45px',
                          }}
                          key={numKey}
                          onClick={() => toggleSelection(numKey)}
                        >
                          {translate(day.toLocaleLowerCase(), { ns: 'maintenance' })}
                        </button>
                      )
                    })}
                  </Box>
                  {form.formState.errors.weekDays && (
                    <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
                      {form.formState.errors.weekDays.message}
                    </Typography>
                  )}
                </Grid>
              )}
              {isCustomMaintenance && isLocalityMaintenance && (
                <Grid item xs={6}>
                  <BasicDatePicker
                    name="startDate"
                    label={translate('startDate', { ns: 'common' })}
                    defaultValue={getUTCDate(new Date())}
                  />
                </Grid>
              )}
              {isCustomMaintenance && isLocalityMaintenance && form.watch('repeatType') != 0 && (
                <Grid item xs={6}>
                  <BasicDatePicker name="repeatEnds" label={translate('repeat-ends', { ns: 'maintenance' })} />
                </Grid>
              )}
              {!isLocalityMaintenance && (
                <Grid item xs={12}>
                  <FormSelectField
                    name="mainComponents"
                    label={translate('equipment', { ns: 'maintenance' })}
                    data={mainComponents?.filter(m => m.cageName != null)?.map(mc => mc.id) ?? []}
                    getOptionLabel={option => {
                      const mainComponent = mainComponents?.find(mc => mc.id === option) as IMainComponent
                      return `${JSON.parse(mainComponent?.typeName)[i18n.language]} - ${mainComponent?.serialNumber}`
                    }}
                    renderOption={(props: any, option) => {
                      const mainComponent = mainComponents?.find(mc => mc.id === option) as IMainComponent
                      return (
                        <Box {...props} key={option}>
                          {JSON.parse(mainComponent?.typeName)[i18n.language]} - {mainComponent?.serialNumber}
                        </Box>
                      )
                    }}
                    defaultValue={[]}
                    isOptionEqualToValue={(o, v) => o === v}
                    multiple
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box
                  sx={{
                    padding: '0 8px 12px',
                    '& .ql-editor': { fontWeight: 400 },
                    '& .ql-editor strong': { fontWeight: 700 },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: '0 !important', paddingBottom: '8px' }}>
                    {translate('note', { ns: 'maintenance' })}
                  </Typography>
                  <ReactQuill
                    value={decode(form.getValues().note || '')}
                    onChange={value => {
                      form.setValue('note', encode(value))
                      form.trigger('note')
                    }}
                    id="note"
                    modules={{
                      clipboard: {
                        matchVisual: true,
                      },
                    }}
                  />
                </Box>
                {form.formState.errors.note && (
                  <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
                    {form.formState.errors.note.message}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormFileCoreUpload
                  FileIcon={FileIcon}
                  label={translate('fileName', { ns: 'common' })}
                  name="fileId"
                  mapFileKey="id"
                  height="175px"
                  isDeletable={true}
                  initialFiles={initialValues?.file}
                  getUploadedFiles={e => getUploadedFiles(e, form)}
                  checkIsFileDeleted={(isFileDeleted, file) => checkIsFileDeleted(isFileDeleted, form)}
                />
              </Grid>
            </Grid>
          </AppForm>
        </Box>
      </GenericDialogLayout>
    </>
  )
}

const getUploadedFiles = (e, form) => {
  form.setValue('file', e)
}

const checkIsFileDeleted = (isDeleted, form) => {
  if (isDeleted) form.setValue('file', null)
}

export default CreateMaintenanceForm
